import * as React from 'react'
import { useState, useMemo, useCallback } from 'react'
import { debounce } from 'lodash'
import SmModalWindow from '../../../shared/modal_window'
import Filters from './filters'
import ElementTable from './element_table'
import CompositElementID from '../../../../models/campaigns/composit_element_id'
import { Tag, ElementItem } from './types'

const { I18n } = window as any

type SelectElementsModalProps = {
  items: ElementItem[]
  onAdd: (items: ElementItem[]) => void
  showImages?: boolean
  isLocation: boolean
  allTags: Tag[]
}

export default function SelectElementsModal(props: SelectElementsModalProps) {
  const { items, onAdd, showImages, isLocation, allTags } = props

  const [visible, setVisible] = useState(false)
  const [nameFilter, setNameFilter] = useState('')
  const [debouncedNameFilter, setDebouncedNameFilter] = useState('')
  const [selectedTags, setSelectedTags] = useState<Tag[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set())

  const debouncedSetNameFilter = useMemo(
    () => debounce((value: string) => setDebouncedNameFilter(value), 300),
    []
  )

  const handleNameFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setNameFilter(value)
      debouncedSetNameFilter(value)
    },
    [debouncedSetNameFilter]
  )

  const handleTagsChange = useCallback((tags: Tag[]) => {
    setSelectedTags(tags || [])
  }, [])

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (debouncedNameFilter) {
        const searchTerm = debouncedNameFilter.toLowerCase()
        const itemName = (item.name || item.label || '').toLowerCase()
        if (!itemName.includes(searchTerm)) {
          return false
        }
      }

      if (selectedTags.length > 0) {
        const itemTagIds = item.tagIds || []
        const hasAllTags = selectedTags.every((tag) =>
          itemTagIds.includes(tag.value)
        )
        if (!hasAllTags) {
          return false
        }
      }

      return true
    })
  }, [items, debouncedNameFilter, selectedTags])

  const allVisibleSelected = useMemo(() => {
    if (filteredItems.length === 0) return false
    return filteredItems.every((item) => selectedIds.has(item.value))
  }, [filteredItems, selectedIds])

  const handleToggleAll = useCallback(() => {
    if (allVisibleSelected) {
      const newSelectedIds = new Set(selectedIds)
      filteredItems.forEach((item) => newSelectedIds.delete(item.value))
      setSelectedIds(newSelectedIds)
    } else {
      const newSelectedIds = new Set(selectedIds)
      filteredItems.forEach((item) => newSelectedIds.add(item.value))
      setSelectedIds(newSelectedIds)
    }
  }, [allVisibleSelected, filteredItems, selectedIds])

  const handleToggleOne = useCallback((id: string | number) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  const handleOpen = () => {
    setVisible(true)
    setNameFilter('')
    setDebouncedNameFilter('')
    setSelectedTags([])
    setSelectedIds(new Set())
  }

  const handleClose = () => {
    setVisible(false)
  }

  const handleAddSelected = (closeAfter: boolean = false) => {
    const selectedItems = items.filter((item) => selectedIds.has(item.value))
    if (selectedItems.length > 0) {
      const compositIds = selectedItems.map(
        (item) => new CompositElementID(item.campaignElementID, item.value, item.type, null)
      )
      onAdd(compositIds)
      // Clear selections after adding
      setSelectedIds(new Set())
    }
    if (closeAfter) {
      handleClose()
    }
  }

  const modalContent = (
    <div>
      <Filters
        nameFilter={nameFilter}
        onNameFilterChange={handleNameFilterChange}
        selectedTags={selectedTags}
        onTagsChange={handleTagsChange}
        allTags={allTags}
      />
      <ElementTable
        items={filteredItems}
        selectedIds={selectedIds}
        onToggleOne={handleToggleOne}
        onToggleAll={handleToggleAll}
        allSelected={allVisibleSelected}
        showImages={showImages}
      />
      {selectedIds.size > 0 && (
        <div className="mt-2 text-muted">
          {I18n.t('campaigns.others.selected_count', { count: selectedIds.size })}
        </div>
      )}
    </div>
  )

  const modalButtons = (
    <>
      <button type="button" className="btn btn-secondary" onClick={handleClose}>
        {I18n.t('application.actions.cancel')}
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => handleAddSelected(false)}
        disabled={selectedIds.size === 0}
      >
        {I18n.t('campaigns.others.add_selected')}
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => handleAddSelected(true)}
        disabled={selectedIds.size === 0}
      >
        {I18n.t('campaigns.others.add_and_close')}
      </button>
    </>
  )

  const windowID = isLocation ? 'select_location_elements_modal' : 'select_common_elements_modal'

  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={handleOpen}>
        {I18n.t('campaigns.others.select_elements')}
      </button>
      <SmModalWindow
        title={I18n.t('campaigns.others.select_elements')}
        visible={visible}
        onClose={handleClose}
        content={modalContent}
        buttons={modalButtons}
        windowID={windowID}
        size="xl"
      />
    </>
  )
}
